import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

// Public site key — safe to hardcode (not a secret, same as a Stripe/Mapbox publishable key).
// One site key covers every environment; verification (where the real gate is) happens
// server-side against the secret key, not here. See BAC-1820.
const TURNSTILE_SITE_KEY = '0x4AAAAAAFMPEfNmFNQqslps'
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js'

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string
          action?: string
          callback: (token: string) => void
          'error-callback'?: () => void
          'expired-callback'?: () => void
        },
      ) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
  }
}

let scriptLoadPromise: Promise<void> | null = null

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve()
  if (scriptLoadPromise) return scriptLoadPromise
  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = TURNSTILE_SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Turnstile script'))
    document.head.appendChild(script)
  })
  return scriptLoadPromise
}

export interface TurnstileHandle {
  // Turnstile tokens are single-use — call this after a failed submit so the widget issues a
  // fresh one before the next attempt, instead of forcing a full remount.
  reset: () => void
}

interface TurnstileWidgetProps {
  // 1-32 chars (letters/numbers/underscores/hyphens) identifying the protected surface —
  // checked against siteverify's `action` response field server-side.
  action: string
  onToken: (token: string | null) => void
}

// Explicit (JS-driven) rendering rather than the implicit cf-turnstile div, since the token needs
// to reach two different submit paths (the email form's onSubmit, and the Google button's
// onClick, which isn't a form submit at all) and the widget needs to be resettable after a
// failed signup attempt.
export const TurnstileWidget = forwardRef<TurnstileHandle, TurnstileWidgetProps>(function TurnstileWidget(
  { action, onToken },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const [failedToLoad, setFailedToLoad] = useState(false)

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current && window.turnstile) window.turnstile.reset(widgetIdRef.current)
    },
  }))

  useEffect(() => {
    let cancelled = false

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          action,
          callback: (token) => onToken(token),
          'error-callback': () => onToken(null),
          'expired-callback': () => onToken(null),
        })
      })
      .catch(() => setFailedToLoad(true))

    return () => {
      cancelled = true
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (failedToLoad) return null

  return <div ref={containerRef} />
})
